import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MessageSquare, X, Send, Loader2, Bot, Phone, FileText, Store, UserCog, Sparkles } from "lucide-react";
import { API } from "../lib/api";
import { useLead } from "../context/LeadContext";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const WA = "https://wa.me/919499799598";

const SUGGESTIONS = [
  "Explore Genesis Mall",
  "Explore Our Businesses",
  "Become an Affiliate",
  "Business Opportunity",
  "Contact Genesis",
];

export const ChatWidget = () => {
  const navigate = useNavigate();
  const { openLead } = useLead();
  const [open, setOpen] = useState(false);
  const [sid] = useState(() => localStorage.getItem("ld_chat_sid") || (() => { const s = uid(); localStorage.setItem("ld_chat_sid", s); return s; })());
  const [messages, setMessages] = useState([{ role: "assistant", content: "Namaste! 🙏 Welcome to Genesis Group & Genesis Mall — your All-in-One Business Center. How can we help you today?" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [human, setHuman] = useState(false);
  const [hName, setHName] = useState("");
  const [hPhone, setHPhone] = useState("");
  const [hSent, setHSent] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, human]);

  const pushAssistant = (content) => setMessages((m) => [...m, { role: "assistant", content }]);

  const send = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || busy) return;
    if (!preset) setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid, message: text }) });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const j = JSON.parse(data);
            if (j.delta) setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + j.delta }; return c; });
          } catch {}
        }
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Sorry, I'm offline right now. Please call +91 94997 99598." }; return c; });
    } finally { setBusy(false); }
  };

  const startHuman = () => {
    setHuman(true);
    setHSent(false);
    pushAssistant("Sure! You can reach our team instantly on 📞 +91 94997 99598 or WhatsApp. Leave your name and number below and we'll call you right back.");
  };

  const submitHuman = async () => {
    if (!hName.trim() || hPhone.trim().length < 10) return;
    const transcript = messages.filter((m) => m.content).map((m) => `${m.role}: ${m.content}`).join(" | ");
    try {
      await fetch(`${API}/chat/handover`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid, name: hName, phone: hPhone, transcript }) });
      setHSent(true);
      pushAssistant(`Thank you, ${hName.trim()}! Our team will call you on ${hPhone.trim()} shortly. 🙏`);
      setHuman(false);
    } catch {
      pushAssistant("Couldn't submit right now — please call us directly at +91 94997 99598.");
    }
  };

  const actions = [
    { label: "Explore Genesis Mall", icon: Store, onClick: () => { setOpen(false); navigate("/mall"); }, testid: "chat-action-mall" },
    { label: "Become an Affiliate", icon: Sparkles, onClick: () => { setOpen(false); navigate("/opportunity"); }, testid: "chat-action-affiliate" },
    { label: "Talk to a Human", icon: UserCog, onClick: startHuman, testid: "chat-action-human" },
    { label: "Investor Deck", icon: FileText, onClick: () => window.open("/genesis-pitch-deck.pdf", "_blank"), testid: "chat-action-deck" },
  ];

  return (
    <div data-no-translate>
      <button onClick={() => setOpen((v) => !v)} data-testid="chat-toggle-button"
        className="fixed bottom-5 left-4 z-50 w-14 h-14 rounded-full gold-gradient flex items-center justify-center shadow-lg gold-glow hover:scale-110 transition-transform">
        {open ? <X className="w-6 h-6 text-obsidian-900" /> : <MessageSquare className="w-6 h-6 text-obsidian-900" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.96 }} transition={{ duration: 0.25 }}
            className="fixed bottom-24 left-4 z-50 w-[92vw] max-w-sm h-[72vh] max-h-[600px] card-dark rounded-2xl border border-gold/30 flex flex-col overflow-hidden shadow-2xl" data-testid="chat-window">
            <div className="gold-gradient px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-obsidian-900/20 flex items-center justify-center"><Bot className="w-5 h-5 text-obsidian-900" /></div>
              <div><div className="font-heading font-bold text-obsidian-900 leading-none">Genesis Assistant</div><div className="text-[11px] text-obsidian-900/70">Online · replies in seconds</div></div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`chat-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-gold/20 text-white rounded-br-sm" : "bg-obsidian-900/70 text-slate-200 rounded-bl-sm border border-white/10"}`}>
                    {m.content || (busy && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-gold" /> : "")}
                  </div>
                </div>
              ))}

              {/* Suggestion chips (only early in the conversation) */}
              {messages.length <= 2 && !busy && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} className="text-xs text-gold border border-gold/40 rounded-full px-3 py-1.5 hover:bg-gold/10 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Talk-to-human inline form */}
              {human && !hSent && (
                <div className="rounded-xl border border-gold/30 bg-obsidian-900/70 p-3 space-y-2" data-testid="chat-human-form">
                  <input value={hName} onChange={(e) => setHName(e.target.value)} placeholder="Your name" data-testid="chat-human-name"
                    className="w-full bg-obsidian-900/60 border border-white/10 rounded-lg px-3 h-9 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-gold/40" />
                  <input value={hPhone} onChange={(e) => setHPhone(e.target.value.replace(/[^\d]/g, "").slice(0, 10))} placeholder="Mobile number" data-testid="chat-human-phone"
                    className="w-full bg-obsidian-900/60 border border-white/10 rounded-lg px-3 h-9 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-gold/40" />
                  <div className="flex gap-2">
                    <button onClick={submitHuman} data-testid="chat-human-submit" className="flex-1 gold-gradient text-obsidian-900 text-sm font-semibold rounded-lg h-9">Request a call back</button>
                    <a href={WA} target="_blank" rel="noreferrer nofollow" className="h-9 px-3 flex items-center justify-center rounded-lg border border-gold/40 text-gold text-sm"><Phone className="w-4 h-4" /></a>
                  </div>
                </div>
              )}

              <div ref={endRef} />
            </div>

            {/* Action buttons */}
            <div className="px-3 pt-2 flex flex-wrap gap-1.5 border-t border-white/10">
              {actions.map((a) => (
                <button key={a.label} onClick={a.onClick} data-testid={a.testid}
                  className="flex items-center gap-1 text-[11px] text-slate-200 bg-obsidian-900/70 border border-white/10 rounded-full px-2.5 py-1.5 hover:border-gold/40 hover:text-gold transition-colors">
                  <a.icon className="w-3 h-3" /> {a.label}
                </button>
              ))}
            </div>

            <div className="p-3 flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="chat-input"
                placeholder="Ask about Genesis Mall, businesses…" className="flex-1 bg-obsidian-900/60 border border-white/10 rounded-full px-4 h-10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-gold/40" />
              <button onClick={() => send()} disabled={busy} data-testid="chat-send-button" className="w-10 h-10 rounded-full gold-gradient flex items-center justify-center shrink-0 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin text-obsidian-900" /> : <Send className="w-4 h-4 text-obsidian-900" />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
